import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  ApiError,
  fileToBase64,
  uploadIntakeBrandAsset,
  type IntakeUploadKind,
  type IntakeUploadResponse,
} from '../../../lib/api.js';
import {
  describeHexError,
  validateHexColor,
} from '../../../lib/brand-kit-form.js';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.3 — Step 2 "Brand kit" for the prospect-facing intake portal.
 *
 * Fields mirror `intakeStep2Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   logo: { primarySvg, primaryPngLight, primaryPngDark?, favicon, emailHeader? }
 *   colors: { primary, secondary, accent, background?, text? }
 *   typography: { primaryFontFamily, headingFontFamily } — curated enum
 *   copy: { companyName, tagline?, supportEmail, supportPhone? }
 *   terminology: Record<string,string> (up to 20 entries)
 *
 * Logo uploads POST base64 bytes to `/api/intake/:token/upload` (D2.2.4).
 * That endpoint exposes four kinds that align with the schema:
 *
 *   primarySvg       → 'logo-primary-svg'
 *   primaryPngLight  → 'logo-primary-png'
 *   favicon          → 'favicon-png'
 *   emailHeader      → 'logo-email-header-png'
 *
 * `primaryPngDark` is optional in the schema but the v1 endpoint does not
 * yet have a matching `logo-primary-png-dark` kind — the dark-mode upload
 * slot is intentionally deferred and documented in the handoff notes. The
 * three required slots + the optional email header cover 100% of the
 * required-field surface the server validator gates.
 *
 * On a successful upload the slot's payload gets a `{ blobKey, publicUrl,
 * mimeType, sizeBytes }` ref exactly as `blobUploadRefSchema` expects.
 */

type BlobRef = {
  blobKey: string;
  publicUrl: string;
  mimeType: string;
  sizeBytes: number;
};

type LogoSlot = 'primarySvg' | 'primaryPngLight' | 'favicon' | 'emailHeader';

type SlotDef = {
  slot: LogoSlot;
  kind: IntakeUploadKind;
  mimeType: string;
  label: string;
  hint: string;
  accept: string;
  required: boolean;
};

export const INTAKE_LOGO_SLOT_DEFS: ReadonlyArray<SlotDef> = [
  {
    slot: 'primarySvg',
    kind: 'logo-primary-svg',
    mimeType: 'image/svg+xml',
    label: 'Primary logo (SVG)',
    hint: 'Used in the admin chrome and customer header. Parseable SVG.',
    accept: 'image/svg+xml,.svg',
    required: true,
  },
  {
    slot: 'primaryPngLight',
    kind: 'logo-primary-png',
    mimeType: 'image/png',
    label: 'Primary logo (PNG)',
    hint: 'High-resolution raster fallback. At least 1024 × 1024 px.',
    accept: 'image/png,.png',
    required: true,
  },
  {
    slot: 'favicon',
    kind: 'favicon-png',
    mimeType: 'image/png',
    label: 'Favicon (PNG)',
    hint: 'Browser tab icon. Must be 16×16 or 32×32 px.',
    accept: 'image/png,.png',
    required: true,
  },
  {
    slot: 'emailHeader',
    kind: 'logo-email-header-png',
    mimeType: 'image/png',
    label: 'Email header (PNG) — optional',
    hint: 'Top of transactional emails. At least 1024 × 1024 px.',
    accept: 'image/png,.png',
    required: false,
  },
];

export const INTAKE_CURATED_FONTS = [
  'Inter',
  'Manrope',
  'Open Sans',
  'Lato',
  'Nunito',
  'Playfair Display',
  'Fraunces',
  'Cormorant Garamond',
  'Poppins',
  'DM Sans',
  'Work Sans',
  'Montserrat',
  'Merriweather',
  'Source Serif Pro',
] as const;

export type IntakeCuratedFont = (typeof INTAKE_CURATED_FONTS)[number];

const COLOR_KEYS = ['primary', 'secondary', 'accent', 'background', 'text'] as const;
type ColorKey = (typeof COLOR_KEYS)[number];

const COLOR_LABEL: Record<ColorKey, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  accent: 'Accent',
  background: 'Background',
  text: 'Text',
};

const DEFAULT_BG = '#FFFFFF';
const DEFAULT_TEXT = '#1A1A1A';

const MAX_TERMINOLOGY_ENTRIES = 20;

type UploadStatus =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  | { kind: 'uploaded' }
  | { kind: 'error'; message: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readBlobRef(value: unknown): BlobRef | undefined {
  if (!isRecord(value)) return undefined;
  const blobKey = stringField(value.blobKey);
  const publicUrl = stringField(value.publicUrl);
  const mimeType = stringField(value.mimeType);
  const sizeBytes =
    typeof value.sizeBytes === 'number' && Number.isFinite(value.sizeBytes)
      ? value.sizeBytes
      : 0;
  if (blobKey === '' || publicUrl === '') return undefined;
  return { blobKey, publicUrl, mimeType, sizeBytes };
}

function readLogoMap(value: unknown): Partial<Record<LogoSlot, BlobRef>> {
  if (!isRecord(value)) return {};
  const out: Partial<Record<LogoSlot, BlobRef>> = {};
  for (const def of INTAKE_LOGO_SLOT_DEFS) {
    const ref = readBlobRef(value[def.slot]);
    if (ref) out[def.slot] = ref;
  }
  return out;
}

function readColors(value: unknown): Partial<Record<ColorKey, string>> {
  if (!isRecord(value)) return {};
  const out: Partial<Record<ColorKey, string>> = {};
  for (const key of COLOR_KEYS) {
    const v = stringField(value[key]);
    if (v !== '') out[key] = v;
  }
  return out;
}

function readTypography(value: unknown): {
  primaryFontFamily: string;
  headingFontFamily: string;
} {
  if (!isRecord(value)) return { primaryFontFamily: '', headingFontFamily: '' };
  return {
    primaryFontFamily: stringField(value.primaryFontFamily),
    headingFontFamily: stringField(value.headingFontFamily),
  };
}

function readCopy(value: unknown): {
  companyName: string;
  tagline: string;
  supportEmail: string;
  supportPhone: string;
} {
  if (!isRecord(value)) {
    return {
      companyName: '',
      tagline: '',
      supportEmail: '',
      supportPhone: '',
    };
  }
  return {
    companyName: stringField(value.companyName),
    tagline: stringField(value.tagline),
    supportEmail: stringField(value.supportEmail),
    supportPhone: stringField(value.supportPhone),
  };
}

function readTerminology(value: unknown): Array<[string, string]> {
  if (!isRecord(value)) return [];
  const out: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(value)) {
    out.push([k, typeof v === 'string' ? v : '']);
  }
  return out;
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function fieldErrorId(field: string): string {
  return `intake-step2-${field}-error`.replace(/[^a-z0-9-]/gi, '-');
}

/** Test seam — matches the shape of `uploadIntakeBrandAsset`. */
export type IntakeUploader = (
  apiUrl: string,
  token: string,
  body: {
    kind: IntakeUploadKind;
    mimeType: string;
    bytesBase64: string;
  },
) => Promise<IntakeUploadResponse>;

export type Step2BrandKitProps = IntakeShellRenderProps & {
  /** Test seam — overrides the network upload path. */
  uploader?: IntakeUploader;
  /** Test seam — overrides File → base64 conversion. */
  readFileAsBase64?: (file: Blob) => Promise<string>;
};

export function Step2BrandKit({
  stepDraft,
  onChange,
  issues,
  apiUrl,
  token,
  uploader = uploadIntakeBrandAsset,
  readFileAsBase64 = fileToBase64,
}: Step2BrandKitProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const logoMap = useMemo(() => readLogoMap(stepDraft.logo), [stepDraft.logo]);
  const colors = useMemo(() => readColors(stepDraft.colors), [stepDraft.colors]);
  const typography = useMemo(
    () => readTypography(stepDraft.typography),
    [stepDraft.typography],
  );
  const copy = useMemo(() => readCopy(stepDraft.copy), [stepDraft.copy]);

  // Terminology editor state. Seeded from the draft exactly once so the
  // prospect can edit freely (including adding empty rows) without every
  // keystroke triggering a read-back through the draft-normalized shape.
  // The draft is updated with only non-empty-key rows so Zod at submit
  // time sees a clean Record<string,string>.
  const [terminologyRows, setTerminologyRows] = useState<Array<[string, string]>>(
    () => readTerminology(stepDraft.terminology),
  );

  const [uploadStatus, setUploadStatus] = useState<Record<LogoSlot, UploadStatus>>({
    primarySvg: { kind: 'idle' },
    primaryPngLight: { kind: 'idle' },
    favicon: { kind: 'idle' },
    emailHeader: { kind: 'idle' },
  });
  const seqRef = useRef<Record<LogoSlot, number>>({
    primarySvg: 0,
    primaryPngLight: 0,
    favicon: 0,
    emailHeader: 0,
  });

  // Seed color defaults for background/text on first mount so the draft
  // round-trips cleanly. The schema defaults these to #FFFFFF/#1A1A1A, but
  // Zod's `.default()` only fires when the key is absent — we surface the
  // defaults in the UI so the prospect sees what's been applied.
  useEffect(() => {
    const patch: Record<ColorKey, string> = {} as Record<ColorKey, string>;
    let changed = false;
    if (!colors.background) {
      patch.background = DEFAULT_BG;
      changed = true;
    }
    if (!colors.text) {
      patch.text = DEFAULT_TEXT;
      changed = true;
    }
    if (changed) {
      onChange({ colors: { ...colors, ...patch } });
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchLogo = (slot: LogoSlot, ref: BlobRef): void => {
    onChange({ logo: { ...logoMap, [slot]: ref } });
  };

  const handleFile = async (slot: LogoSlot, file: File): Promise<void> => {
    const def = INTAKE_LOGO_SLOT_DEFS.find((d) => d.slot === slot)!;
    const seq = ++seqRef.current[slot];
    setUploadStatus((s) => ({ ...s, [slot]: { kind: 'uploading' } }));
    try {
      const bytesBase64 = await readFileAsBase64(file);
      const res = await uploader(apiUrl, token, {
        kind: def.kind,
        mimeType: def.mimeType,
        bytesBase64,
      });
      if (seqRef.current[slot] !== seq) return;
      patchLogo(slot, {
        blobKey: res.path,
        publicUrl: res.url,
        mimeType: def.mimeType,
        sizeBytes: res.size,
      });
      setUploadStatus((s) => ({ ...s, [slot]: { kind: 'uploaded' } }));
    } catch (e) {
      if (seqRef.current[slot] !== seq) return;
      const message =
        e instanceof ApiError
          ? `Upload failed (${e.status}). ${e.message}`
          : 'Upload failed — check your connection and retry.';
      setUploadStatus((s) => ({ ...s, [slot]: { kind: 'error', message } }));
    }
  };

  const onFileChange = (slot: LogoSlot) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const file = e.target.files?.[0];
      if (!file) return;
      void handleFile(slot, file);
    };

  const onColorChange = (key: ColorKey) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      onChange({ colors: { ...colors, [key]: e.target.value } });
    };

  const onFontChange = (
    key: 'primaryFontFamily' | 'headingFontFamily',
  ) =>
    (e: ChangeEvent<HTMLSelectElement>): void => {
      onChange({ typography: { ...typography, [key]: e.target.value } });
    };

  const onCopyChange = (
    key: 'companyName' | 'tagline' | 'supportEmail' | 'supportPhone',
  ) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      onChange({ copy: { ...copy, [key]: e.target.value } });
    };

  const commitTerminology = (next: ReadonlyArray<[string, string]>): void => {
    setTerminologyRows(next.map((entry): [string, string] => [entry[0], entry[1]]));
    onChange({ terminology: serializeTerminology(next) });
  };

  const onTerminologyChange = (idx: number, side: 'key' | 'value', raw: string): void => {
    const next = terminologyRows.map((entry): [string, string] => [entry[0], entry[1]]);
    const current = next[idx] ?? ['', ''];
    if (side === 'key') next[idx] = [raw, current[1]];
    else next[idx] = [current[0], raw];
    commitTerminology(next);
  };

  const onAddTerminology = (): void => {
    if (terminologyRows.length >= MAX_TERMINOLOGY_ENTRIES) return;
    commitTerminology([...terminologyRows, ['', ''] as [string, string]]);
  };

  const onRemoveTerminology = (idx: number): void => {
    commitTerminology(terminologyRows.filter((_, i) => i !== idx));
  };

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  const companyNameError = serverErrorFor('copy.companyName');
  const supportEmailError = serverErrorFor('copy.supportEmail');
  const taglineError = serverErrorFor('copy.tagline');
  const supportPhoneError = serverErrorFor('copy.supportPhone');
  const primaryFontError = serverErrorFor('typography.primaryFontFamily');
  const headingFontError = serverErrorFor('typography.headingFontFamily');
  const terminologyError =
    serverErrorFor('terminology') ?? serverErrorFor('_root');

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 2 — Brand kit"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Upload your logo, pick your brand colors, and set the copy that shows
        up across the customer and admin experiences.
      </p>

      <section
        className="ferro-field"
        aria-labelledby="intake-step2-logos-heading"
      >
        <h2
          id="intake-step2-logos-heading"
          className="ferro-field__label"
        >
          Logos &amp; favicon
        </h2>
        <p className="ferro-muted">
          Files upload to <code>intakes/{token || '«token»'}/</code>. SVG must
          be parseable, primary PNG ≥ 1024 × 1024 px, favicon 16×16 or 32×32 px.
        </p>

        {INTAKE_LOGO_SLOT_DEFS.map((def) => {
          const status = uploadStatus[def.slot];
          const assetRef = logoMap[def.slot];
          const errorPath = `logo.${def.slot}`;
          const serverErr = serverErrorFor(errorPath);
          return (
            <LogoSlotRow
              key={def.slot}
              def={def}
              status={status}
              assetRef={assetRef}
              serverError={serverErr}
              onFileChange={onFileChange(def.slot)}
            />
          );
        })}
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step2-colors-heading"
      >
        <h2
          id="intake-step2-colors-heading"
          className="ferro-field__label"
        >
          Brand colors
        </h2>
        <p className="ferro-muted">
          6-digit hex like <code>#62A2C3</code>. Background and text default
          to white-on-near-black if you don't change them.
        </p>
        {COLOR_KEYS.map((key) => {
          const value = colors[key] ?? (key === 'background'
            ? DEFAULT_BG
            : key === 'text'
              ? DEFAULT_TEXT
              : '');
          const validation = value === '' ? null : validateHexColor(value);
          const formatError
            = validation && !validation.ok ? describeHexError(validation.reason) : null;
          const serverErr = serverErrorFor(`colors.${key}`);
          const id = `intake-step2-color-${key}`;
          return (
            <div className="ferro-field" key={key}>
              <label htmlFor={id} className="ferro-field__label">
                {COLOR_LABEL[key]}
              </label>
              <div
                className="ferro-intake__colorRow"
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
              >
                <span
                  aria-hidden
                  data-testid={`intake-step2-color-swatch-${key}`}
                  style={{
                    display: 'inline-block',
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.18)',
                    background: validation?.ok ? validation.value : 'transparent',
                  }}
                />
                <input
                  id={id}
                  type="text"
                  name={`intake-step2-color-${key}`}
                  inputMode="text"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="ferro-field__input"
                  value={value}
                  onChange={onColorChange(key)}
                  aria-invalid={formatError || serverErr ? true : undefined}
                  aria-describedby={formatError || serverErr ? fieldErrorId(`color-${key}`) : undefined}
                  data-testid={`intake-step2-color-input-${key}`}
                />
              </div>
              {formatError ? (
                <span
                  id={fieldErrorId(`color-${key}`)}
                  role="alert"
                  className="ferro-form__error"
                >
                  {formatError}
                </span>
              ) : null}
              {serverErr && !formatError ? (
                <span
                  id={fieldErrorId(`color-${key}`)}
                  role="alert"
                  className="ferro-form__error"
                >
                  {serverErr}
                </span>
              ) : null}
            </div>
          );
        })}
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step2-typography-heading"
      >
        <h2
          id="intake-step2-typography-heading"
          className="ferro-field__label"
        >
          Typography
        </h2>
        <p className="ferro-muted">
          Pick from the curated list. Custom fonts can be added later by our
          team after launch.
        </p>

        <label className="ferro-field">
          <span className="ferro-field__label">Body font family</span>
          <select
            name="primaryFontFamily"
            value={typography.primaryFontFamily}
            onChange={onFontChange('primaryFontFamily')}
            className="ferro-field__input"
            required
            aria-invalid={primaryFontError ? true : undefined}
            aria-describedby={
              primaryFontError ? fieldErrorId('primaryFontFamily') : undefined
            }
            data-testid="intake-step2-font-body"
          >
            <option value="">Select a body font…</option>
            {INTAKE_CURATED_FONTS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          {primaryFontError ? (
            <span
              id={fieldErrorId('primaryFontFamily')}
              role="alert"
              className="ferro-form__error"
            >
              {primaryFontError}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Heading font family</span>
          <select
            name="headingFontFamily"
            value={typography.headingFontFamily}
            onChange={onFontChange('headingFontFamily')}
            className="ferro-field__input"
            required
            aria-invalid={headingFontError ? true : undefined}
            aria-describedby={
              headingFontError ? fieldErrorId('headingFontFamily') : undefined
            }
            data-testid="intake-step2-font-heading"
          >
            <option value="">Select a heading font…</option>
            {INTAKE_CURATED_FONTS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          {headingFontError ? (
            <span
              id={fieldErrorId('headingFontFamily')}
              role="alert"
              className="ferro-form__error"
            >
              {headingFontError}
            </span>
          ) : null}
        </label>
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step2-copy-heading"
      >
        <h2
          id="intake-step2-copy-heading"
          className="ferro-field__label"
        >
          Brand copy
        </h2>

        <label className="ferro-field">
          <span className="ferro-field__label">Company name</span>
          <input
            type="text"
            name="companyName"
            required
            value={copy.companyName}
            onChange={onCopyChange('companyName')}
            className="ferro-field__input"
            placeholder="Happy Tail Happy Dog"
            aria-invalid={companyNameError ? true : undefined}
            aria-describedby={
              companyNameError ? fieldErrorId('companyName') : undefined
            }
            data-testid="intake-step2-company-name"
          />
          {companyNameError ? (
            <span
              id={fieldErrorId('companyName')}
              role="alert"
              className="ferro-form__error"
            >
              {companyNameError}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">
            Tagline <span className="ferro-muted">(optional, up to 140 chars)</span>
          </span>
          <input
            type="text"
            name="tagline"
            maxLength={140}
            value={copy.tagline}
            onChange={onCopyChange('tagline')}
            className="ferro-field__input"
            placeholder="Where every tail gets a happy ending."
            aria-invalid={taglineError ? true : undefined}
            aria-describedby={taglineError ? fieldErrorId('tagline') : undefined}
            data-testid="intake-step2-tagline"
          />
          {taglineError ? (
            <span
              id={fieldErrorId('tagline')}
              role="alert"
              className="ferro-form__error"
            >
              {taglineError}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Support email</span>
          <input
            type="email"
            name="supportEmail"
            autoComplete="email"
            required
            value={copy.supportEmail}
            onChange={onCopyChange('supportEmail')}
            className="ferro-field__input"
            placeholder="hello@example.com"
            aria-invalid={supportEmailError ? true : undefined}
            aria-describedby={
              supportEmailError ? fieldErrorId('supportEmail') : undefined
            }
            data-testid="intake-step2-support-email"
          />
          {supportEmailError ? (
            <span
              id={fieldErrorId('supportEmail')}
              role="alert"
              className="ferro-form__error"
            >
              {supportEmailError}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">
            Support phone <span className="ferro-muted">(optional)</span>
          </span>
          <input
            type="tel"
            name="supportPhone"
            autoComplete="tel"
            value={copy.supportPhone}
            onChange={onCopyChange('supportPhone')}
            className="ferro-field__input"
            placeholder="+1 555 555 1234"
            aria-invalid={supportPhoneError ? true : undefined}
            aria-describedby={
              supportPhoneError ? fieldErrorId('supportPhone') : undefined
            }
            data-testid="intake-step2-support-phone"
          />
          {supportPhoneError ? (
            <span
              id={fieldErrorId('supportPhone')}
              role="alert"
              className="ferro-form__error"
            >
              {supportPhoneError}
            </span>
          ) : null}
        </label>
      </section>

      <section
        className="ferro-field"
        aria-labelledby="intake-step2-terminology-heading"
      >
        <h2
          id="intake-step2-terminology-heading"
          className="ferro-field__label"
        >
          Terminology overrides{' '}
          <span className="ferro-muted">(optional, up to {MAX_TERMINOLOGY_ENTRIES})</span>
        </h2>
        <p className="ferro-muted">
          Rename defaults to match how you speak to your customers. Example:
          “customer” → “pet parent”, “booking” → “stay”.
        </p>

        {terminologyRows.map(([key, value], idx) => (
          <div
            key={`terminology-${idx}`}
            className="ferro-field ferro-intake__terminologyRow"
            data-testid={`intake-step2-terminology-row-${idx}`}
          >
            <input
              type="text"
              value={key}
              onChange={(e) => onTerminologyChange(idx, 'key', e.target.value)}
              className="ferro-field__input"
              placeholder="customer"
              aria-label={`Terminology ${idx + 1} key`}
              data-testid={`intake-step2-terminology-key-${idx}`}
            />
            <input
              type="text"
              value={value}
              onChange={(e) => onTerminologyChange(idx, 'value', e.target.value)}
              className="ferro-field__input"
              placeholder="pet parent"
              aria-label={`Terminology ${idx + 1} value`}
              data-testid={`intake-step2-terminology-value-${idx}`}
            />
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={() => onRemoveTerminology(idx)}
              data-testid={`intake-step2-terminology-remove-${idx}`}
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onAddTerminology}
          disabled={terminologyRows.length >= MAX_TERMINOLOGY_ENTRIES}
          data-testid="intake-step2-terminology-add"
        >
          Add terminology override
        </button>
        {terminologyError ? (
          <span className="ferro-form__error" role="alert">
            {terminologyError}
          </span>
        ) : null}
      </section>
    </form>
  );
}

type LogoSlotRowProps = {
  def: SlotDef;
  status: UploadStatus;
  assetRef?: BlobRef;
  serverError: string | null;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

function LogoSlotRow({
  def,
  status,
  assetRef,
  serverError,
  onFileChange,
}: LogoSlotRowProps): JSX.Element {
  const inputId = `intake-step2-logo-${def.slot}`;
  return (
    <div
      className="ferro-field"
      data-testid={`intake-step2-logo-row-${def.slot}`}
    >
      <label htmlFor={inputId} className="ferro-field__label">
        {def.label}
        {!def.required ? (
          <span className="ferro-muted"> (optional)</span>
        ) : null}
      </label>
      <p className="ferro-muted">{def.hint}</p>
      <input
        id={inputId}
        type="file"
        accept={def.accept}
        onChange={onFileChange}
        data-testid={`intake-step2-logo-input-${def.slot}`}
      />
      {status.kind === 'uploading' ? (
        <span
          className="ferro-muted"
          data-testid={`intake-step2-logo-status-${def.slot}`}
        >
          Uploading…
        </span>
      ) : null}
      {(status.kind === 'uploaded' || (status.kind === 'idle' && assetRef)) ? (
        <span
          className="ferro-muted"
          data-testid={`intake-step2-logo-status-${def.slot}`}
        >
          {status.kind === 'uploaded' ? 'Uploaded.' : 'Saved.'}
        </span>
      ) : null}
      {status.kind === 'error' ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid={`intake-step2-logo-error-${def.slot}`}
        >
          {status.message}
        </span>
      ) : null}
      {serverError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid={`intake-step2-logo-servererror-${def.slot}`}
        >
          {serverError}
        </span>
      ) : null}
      {assetRef ? (
        <a
          href={assetRef.publicUrl}
          target="_blank"
          rel="noreferrer"
          className="ferro-muted"
          data-testid={`intake-step2-logo-url-${def.slot}`}
        >
          {assetRef.publicUrl}
        </a>
      ) : null}
    </div>
  );
}

/**
 * Serialize the editor's row array into the canonical
 * Record<string,string> shape the schema expects. Rows with empty keys
 * are dropped because `z.record(z.string(), z.string())` treats the key
 * as the semantic identifier — an empty-key row is a "row in progress"
 * that doesn't belong in the persisted payload yet.
 */
function serializeTerminology(
  entries: ReadonlyArray<[string, string]>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of entries) {
    if (k === '') continue;
    out[k] = v;
  }
  return out;
}
